import React, { useState, useEffect } from 'react';
import Header from './components/common/Header';
import Homepage from './components/common/Homepage';
import RecipeCard from './components/recipes/RecipeCard';
import DrinkCard from './components/drinks/DrinkCard';
import AddRecipeForm from './components/forms/AddRecipeForm';
import AddDrinkForm from './components/forms/AddDrinkForm';
import SearchBar from './components/common/SearchBar';
import WeeklyPlanner from './components/planner/WeeklyPlanner';
import AuthModal from './components/auth/AuthModal';
import PasswordReset from './components/auth/PasswordReset';
import RecipeDetailModal from './components/recipes/RecipeDetailModal';
import DrinkDetailModal from './components/drinks/DrinkDetailModal';
import { recipeAPI, drinkAPI, authAPI } from './utils/api';
import './App.css';

// Temporary debug - check if env vars are loaded
console.log('App.js - Environment check:');
console.log('REACT_APP_SUPABASE_URL:', process.env.REACT_APP_SUPABASE_URL);
console.log('REACT_APP_SUPABASE_ANON_KEY:', process.env.REACT_APP_SUPABASE_ANON_KEY ? 'Present' : 'Missing');

function App() {
  const [currentSection, setCurrentSection] = useState('dinner');
  const [recipes, setRecipes] = useState([]);
  const [drinks, setDrinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAlcoholicOnly, setShowAlcoholicOnly] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [selectedDrink, setSelectedDrink] = useState(null);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [showDrinkModal, setShowDrinkModal] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  // Check for authentication tokens in URL (magic link or password reset)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const type = urlParams.get('type');
    
    if (type === 'recovery') {
      setShowPasswordReset(true);
    }
    // Magic link authentication is handled automatically by Supabase
    // The auth state change listener will pick up the new session
  }, []);

  // Initialize authentication and load data
  useEffect(() => {
    initializeAuth();
  }, []);

  // Load data when user changes
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const initializeAuth = async () => {
    try {
      // Check if user is already signed in
      const currentUser = await authAPI.getCurrentUser();
      setUser(currentUser);
      
      // Set up auth state listener
      const { data: { subscription } } = authAPI.onAuthStateChange((event, session) => {
        setUser(session?.user || null);
        // Data will be loaded by the useEffect that watches user changes
      });

      // Load initial data if user exists
      if (currentUser) {
        await loadData();
      } else {
        setLoading(false);
      }
      
      return () => {
        subscription?.unsubscribe();
      };
    } catch (error) {
      console.error('Auth initialization error:', error);
    } finally {
      setAuthLoading(false);
    }
  };

  const loadData = async () => {
    // Only load data if user is logged in
    if (!user) {
      setRecipes([]);
      setDrinks([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const [recipesData, drinksData] = await Promise.all([
        recipeAPI.getAll(),
        drinkAPI.getAll()
      ]);
      
      // Filter to only show user's own recipes and drinks
      const userRecipes = (recipesData || []).filter(recipe => recipe.user_id === user.id);
      const userDrinks = (drinksData || []).filter(drink => drink.user_id === user.id);
      
      setRecipes(userRecipes);
      setDrinks(userDrinks);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please check your database connection.');
      setRecipes([]);
      setDrinks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRecipeClick = (recipe) => {
    setSelectedRecipe(recipe);
    setShowRecipeModal(true);
  };

  const handleDrinkClick = (drink) => {
    setSelectedDrink(drink);
    setShowDrinkModal(true);
  };

  const handleAddClick = () => {
    setShowAddForm(true);
  };

  const handleRecipeAdded = (newRecipe) => {
    setRecipes(prev => [newRecipe, ...prev]);
  };

  const handleDrinkAdded = (newDrink) => {
    setDrinks(prev => [newDrink, ...prev]);
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
  };

  const handleSignInClick = () => {
    setShowAuthModal(true);
  };

  const handleAuthSuccess = () => {
    // User will be updated via the auth state listener
    // Data will be loaded by the useEffect that watches user changes
  };

  const handleSignOut = () => {
    setUser(null);
    setRecipes([]);
    setDrinks([]);
    setCurrentSection('dinner'); // Reset to default section
  };

  const handleRecipeUpdated = (updatedRecipe) => {
    setRecipes(prev => prev.map(recipe => 
      recipe.id === updatedRecipe.id ? updatedRecipe : recipe
    ));
    setSelectedRecipe(updatedRecipe);
  };

  const handleScheduleSuccess = () => {
    // This could be used to refresh calendar data or show a success message
    // For now, we'll just log it
    console.log('Recipe scheduled successfully');
  };

  const handleDrinkUpdated = (updatedDrink) => {
    setDrinks(prev => prev.map(drink => 
      drink.id === updatedDrink.id ? updatedDrink : drink
    ));
    setSelectedDrink(updatedDrink);
  };

  const closeRecipeModal = () => {
    setShowRecipeModal(false);
    setSelectedRecipe(null);
  };

  const closeDrinkModal = () => {
    setShowDrinkModal(false);
    setSelectedDrink(null);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handlePasswordResetComplete = () => {
    setShowPasswordReset(false);
    // Clear URL parameters
    window.history.replaceState({}, document.title, window.location.pathname);
    // User should now be authenticated, so load data
    loadData();
  };

  // Show password reset screen if needed
  if (showPasswordReset) {
    return <PasswordReset onResetComplete={handlePasswordResetComplete} />;
  }

  // Filter recipes and drinks based on search query
  const filteredRecipes = searchQuery
    ? recipes.filter(recipe =>
        recipe.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        recipe.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        recipe.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : recipes;

  // Filter drinks based on alcoholic preference and search query
  let filteredDrinks = showAlcoholicOnly 
    ? drinks.filter(drink => drink.is_alcoholic)
    : drinks;

  if (searchQuery) {
    filteredDrinks = filteredDrinks.filter(drink =>
      drink.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      drink.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      drink.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }

  // Show homepage if no user is logged in
  if (!user && !authLoading) {
    return (
      <div className="App">
        <Header 
          currentSection={currentSection} 
          onSectionChange={setCurrentSection}
          user={user}
          onSignOut={handleSignOut}
          onSignInClick={handleSignInClick}
        />
        <Homepage onSignInClick={handleSignInClick} />
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onAuthSuccess={handleAuthSuccess}
        />
      </div>
    );
  }

  if (authLoading || loading) {
    return (
      <div className="App">
        <Header 
          currentSection={currentSection} 
          onSectionChange={setCurrentSection}
          user={user}
          onSignOut={handleSignOut}
          onSignInClick={handleSignInClick}
        />
        <main className="main-content">
          <div className="container">
            <div className="loading">Loading...</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      <Header 
        currentSection={currentSection} 
        onSectionChange={setCurrentSection}
        user={user}
        onSignOut={handleSignOut}
        onSignInClick={handleSignInClick}
      />
      
      <main className="main-content">
        {currentSection === 'planner' ? (
          <WeeklyPlanner user={user} />
        ) : (
          <div className="container">
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            <div className="section-header">
              <h2>{currentSection === 'dinner' ? 'Dinner Recipes' : 'Drink Recipes'}</h2>
              <div className="header-actions">
              {currentSection === 'drink' && (
                <label className="alcoholic-toggle">
                  <input
                    type="checkbox"
                    checked={showAlcoholicOnly}
                    onChange={(e) => setShowAlcoholicOnly(e.target.checked)}
                  />
                  <span>Alcoholic only</span>
                </label>
              )}
              <button className="add-button" onClick={handleAddClick}>
                + Add {currentSection === 'dinner' ? 'Recipe' : 'Drink'}
              </button>
            </div>
          </div>
          
          <SearchBar 
            onSearch={handleSearch}
            placeholder={`Search ${currentSection === 'dinner' ? 'recipes' : 'drinks'}...`}
          />
          
          <div className="cards-grid">
            {currentSection === 'dinner' ? (
              filteredRecipes.length > 0 ? (
                filteredRecipes.map(recipe => (
                  <RecipeCard 
                    key={recipe.id} 
                    recipe={recipe} 
                    onClick={handleRecipeClick}
                    onScheduleSuccess={handleScheduleSuccess}
                  />
                ))
              ) : (
                <div className="empty-state">
                  <p>{searchQuery ? `No recipes found for "${searchQuery}"` : 'No recipes yet. Add your first recipe!'}</p>
                </div>
              )
            ) : (
              filteredDrinks.length > 0 ? (
                filteredDrinks.map(drink => (
                  <DrinkCard 
                    key={drink.id} 
                    drink={drink} 
                    onClick={handleDrinkClick}
                  />
                ))
              ) : (
                <div className="empty-state">
                  <p>
                    {searchQuery 
                      ? `No ${showAlcoholicOnly ? 'alcoholic ' : ''}drinks found for "${searchQuery}"`
                      : `No ${showAlcoholicOnly ? 'alcoholic ' : ''}drinks yet. Add your first drink recipe!`
                    }
                  </p>
                </div>
              )
            )}
          </div>
          </div>
        )}
      </main>
      
      {showAddForm && currentSection === 'dinner' && (
        <AddRecipeForm 
          key="recipe-form"
          onClose={handleCloseForm}
          onRecipeAdded={handleRecipeAdded}
        />
      )}
      
      {showAddForm && currentSection === 'drink' && (
        <AddDrinkForm 
          key="drink-form"
          onClose={handleCloseForm}
          onDrinkAdded={handleDrinkAdded}
        />
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={handleAuthSuccess}
      />

      {selectedRecipe && (
        <RecipeDetailModal
          recipe={selectedRecipe}
          isOpen={showRecipeModal}
          onClose={closeRecipeModal}
          onRecipeUpdated={handleRecipeUpdated}
          user={user}
          onScheduleSuccess={handleScheduleSuccess}
        />
      )}

      {selectedDrink && (
        <DrinkDetailModal
          drink={selectedDrink}
          isOpen={showDrinkModal}
          onClose={closeDrinkModal}
          onDrinkUpdated={handleDrinkUpdated}
          user={user}
        />
      )}
    </div>
  );
}

export default App;
